import {useEffect, useCallback, useRef, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {GlobalBannerActions, GlobalBannerNotification} from './GlobalBannerSlice';
import useAuth from '../../hooks/useAuth';
import {ROUTES} from '../../constants/routes';
import {
  useIsOnboardingComplete,
  useLogTouchpoint,
  useOnboardingResumeStep,
  usePromptableTouchpoints,
  useTouchpointSummaries,
  TouchpointSlug,
  TouchpointSummary,
} from '../../hooks/useTouchpointTracking';
import {useProfileFieldsStatus, ProfileFieldsStatus} from '../../hooks/useProfileFieldsStatus';

/**
 * Get dynamic banner text based on the next onboarding step.
 * All messages end with "to get more connected"
 */
const getOnboardingBannerText = (resumeStep: number | null): string => {
  switch (resumeStep) {
    case 7:
      return 'Put in your home address to get more connected';
    case 8:
      return 'Create your username to get more connected';
    case 9:
      return 'Add a profile picture to get more connected';
    case 10:
      return 'Bookmark ahead to get more connected';
    case 11:
      return 'Set up your notifications to get more connected';
    default:
      // Default for steps 1-6 or unknown
      return 'Complete onboarding to get more connected';
  }
};

type BannerCondition = {
  id: string;
  /** The touchpoint slug this banner corresponds to (optional) */
  touchpointSlug?: TouchpointSlug;
  check: (context: BannerContext) => boolean;
  /** Banner can be static or a function that returns banner based on context */
  banner: Omit<GlobalBannerNotification, 'id'> | ((context: BannerContext) => Omit<GlobalBannerNotification, 'id'>);
  /** Custom action handler - receives context for navigation, etc */
  onAction?: (context: BannerContext) => void;
};

type BannerContext = {
  user: any;
  isAuthenticated: boolean;
  isOnboardingComplete: boolean;
  isOnboardingLoading: boolean;
  navigate: (path: string) => void;
  isOnOnboardingRoute: boolean;
  isOnExcludedRoute: boolean;
  logTouchpoint: (slug: TouchpointSlug, action: 'prompt' | 'access' | 'complete') => void;
  /** The next incomplete onboarding step (URL step number, 7-11) */
  resumeStep: number | null;
  /** Profile field status for checking actual field values */
  profileFields: ProfileFieldsStatus;
  /** Touchpoints that are due for prompting (recurring) */
  promptableTouchpoints: TouchpointSummary[];
  /** All touchpoint summaries for checking completion status */
  touchpointSummaries: TouchpointSummary[];
};

/**
 * Routes where the GlobalBanner should not be displayed.
 * Chat routes have full-screen layouts that don't work well with the banner.
 */
const EXCLUDED_BANNER_ROUTES = [
  '/chats',
  '/chat',
  '/lifestyle/chat',
  '/bookmark-ahead',
];

/**
 * Define all your conditional banners here.
 * Each banner has a condition check and the banner content to show.
 * 
 * PRIORITY ORDER (only one banner shows at a time):
 * 1. Per-field banners (address > handle > photo) - shows when field needs attention
 * 2. Complete onboarding fallback - shows for users with no per-field banners but incomplete onboarding
 */
const BANNER_CONDITIONS: BannerCondition[] = [
  // Per-field banner: Home address
  // Shows when address needs initial entry OR 90-day re-confirmation is due
  {
    id: 'confirm-home-address',
    touchpointSlug: 'home_address_confirmation',
    check: ({isAuthenticated, isOnboardingLoading, isOnOnboardingRoute, isOnExcludedRoute, profileFields, promptableTouchpoints}) => {
      if (isOnboardingLoading || profileFields.isLoading) return false;
      if (isOnExcludedRoute || isOnOnboardingRoute) return false;
      if (!isAuthenticated) return false;
      
      // Show if address is missing
      if (!profileFields.home_address.has_value) return true;
      
      // Show if 90-day confirmation is due (touchpoint is in promptable list)
      const isConfirmationDue = promptableTouchpoints.some(
        (t) => t.touchpoint === 'home_address_confirmation'
      );
      return isConfirmationDue;
    },
    banner: ({profileFields}) => ({
      text: profileFields.home_address.has_value
        ? 'Is your home address still correct? Confirm it to stay connected'
        : 'Add your home address to get more connected',
      variant: 'neutral',
      actions: [{label: 'Go', variant: 'primary', actionId: 'confirm-home-address'}],
      durationSeconds: null,
      dismissible: true,
    }),
    onAction: ({navigate, logTouchpoint}) => {
      logTouchpoint('home_address_confirmation', 'access');
      navigate(`${ROUTES.ONBOARDING}?step=7&from=banner`);
    },
  },

  // Per-field banner: Screen name / username
  // One-time prompt: shows until touchpoint is completed (even if user confirms without changing)
  {
    id: 'confirm-screen-name',
    touchpointSlug: 'user_handle_update',
    check: ({isAuthenticated, isOnboardingLoading, isOnOnboardingRoute, isOnExcludedRoute, profileFields, promptableTouchpoints, touchpointSummaries}) => {
      if (isOnboardingLoading || profileFields.isLoading) return false;
      if (isOnExcludedRoute || isOnOnboardingRoute) return false;
      if (!isAuthenticated) return false;
      
      // Don't show if higher priority banner (address) should show
      const shouldShowAddressBanner = !profileFields.home_address.has_value || 
        promptableTouchpoints.some((t) => t.touchpoint === 'home_address_confirmation');
      if (shouldShowAddressBanner) return false;
      
      // Show if touchpoint has never been completed (one-time prompt)
      const handleTouchpoint = touchpointSummaries.find((t) => t.touchpoint === 'user_handle_update');
      const isCompleted = (handleTouchpoint?.completed_count ?? 0) > 0;
      return !isCompleted;
    },
    banner: ({profileFields}) => ({
      text: profileFields.handle.has_value
        ? 'Confirm your screen name to get more connected'
        : 'Create your screen name to get more connected',
      variant: 'neutral',
      actions: [{label: 'Go', variant: 'primary', actionId: 'confirm-screen-name'}],
      durationSeconds: null,
      dismissible: true,
    }),
    onAction: ({navigate, logTouchpoint}) => {
      logTouchpoint('user_handle_update', 'access');
      navigate(`${ROUTES.ONBOARDING}?step=8&from=banner`);
    },
  },

  // Per-field banner: Profile photo
  // One-time prompt: shows until touchpoint is completed (even if user confirms without changing)
  {
    id: 'confirm-profile-photo',
    touchpointSlug: 'profile_picture_update',
    check: ({isAuthenticated, isOnboardingLoading, isOnOnboardingRoute, isOnExcludedRoute, profileFields, promptableTouchpoints, touchpointSummaries}) => {
      if (isOnboardingLoading || profileFields.isLoading) return false;
      if (isOnExcludedRoute || isOnOnboardingRoute) return false;
      if (!isAuthenticated) return false;
      
      // Don't show if higher priority banners should show
      const shouldShowAddressBanner = !profileFields.home_address.has_value || 
        promptableTouchpoints.some((t) => t.touchpoint === 'home_address_confirmation');
      // Check handle touchpoint completion, not field value
      const handleTouchpoint = touchpointSummaries.find((t) => t.touchpoint === 'user_handle_update');
      const isHandleCompleted = (handleTouchpoint?.completed_count ?? 0) > 0;
      if (shouldShowAddressBanner || !isHandleCompleted) return false;
      
      // Show if touchpoint has never been completed (one-time prompt)
      const photoTouchpoint = touchpointSummaries.find((t) => t.touchpoint === 'profile_picture_update');
      const isCompleted = (photoTouchpoint?.completed_count ?? 0) > 0;
      return !isCompleted;
    },
    banner: ({profileFields}) => ({
      text: profileFields.profile_photo.has_value
        ? 'Confirm your profile photo to get more connected'
        : 'Add a profile photo to get more connected',
      variant: 'neutral',
      actions: [{label: 'Go', variant: 'primary', actionId: 'confirm-profile-photo'}],
      durationSeconds: null,
      dismissible: true,
    }),
    onAction: ({navigate, logTouchpoint}) => {
      logTouchpoint('profile_picture_update', 'access');
      navigate(`${ROUTES.ONBOARDING}?step=9&from=banner`);
    },
  },

  // Fallback: Complete onboarding banner
  // Shows for users who haven't completed onboarding AND none of the per-field banners are showing
  // This catches edge cases like users who need bookmark/notification setup
  {
    id: 'complete-onboarding',
    touchpointSlug: 'onboarding_flow',
    check: ({isAuthenticated, isOnboardingComplete, isOnboardingLoading, isOnOnboardingRoute, isOnExcludedRoute, profileFields, promptableTouchpoints, touchpointSummaries}) => {
      // Don't show while loading
      if (isOnboardingLoading || profileFields.isLoading) return false;
      // Don't show on excluded routes (chat pages, etc.)
      if (isOnExcludedRoute) return false;
      // Don't show if they're currently on the onboarding route
      if (isOnOnboardingRoute) return false;
      // Don't show if onboarding is already complete
      if (isOnboardingComplete) return false;
      // Only show if authenticated
      if (!isAuthenticated) return false;
      
      // Don't show if any per-field banner would show (they have priority)
      // Address: missing OR 90-day confirmation due
      const shouldShowAddressBanner = !profileFields.home_address.has_value || 
        promptableTouchpoints.some((t) => t.touchpoint === 'home_address_confirmation');
      // Handle/Photo: check touchpoint completion (one-time prompt)
      const handleTouchpoint = touchpointSummaries.find((t) => t.touchpoint === 'user_handle_update');
      const photoTouchpoint = touchpointSummaries.find((t) => t.touchpoint === 'profile_picture_update');
      const shouldShowHandleBanner = (handleTouchpoint?.completed_count ?? 0) === 0;
      const shouldShowPhotoBanner = (photoTouchpoint?.completed_count ?? 0) === 0;
      
      if (shouldShowAddressBanner || shouldShowHandleBanner || shouldShowPhotoBanner) {
        return false;
      }
      
      // Show fallback for users with all profile fields but incomplete onboarding
      return true;
    },
    banner: ({resumeStep}) => ({
      text: getOnboardingBannerText(resumeStep),
      variant: 'neutral',
      actions: [{label: 'Go', variant: 'primary', actionId: 'complete-onboarding'}],
      durationSeconds: null,
      dismissible: true,
    }),
    onAction: ({navigate, logTouchpoint}) => {
      // Log that user accessed the onboarding flow via banner
      logTouchpoint('onboarding_flow', 'access');
      // Navigate to onboarding with from=banner to indicate source
      // The firstIncompleteStepUrl will be used by useOnboardingProgress hook
      navigate(`${ROUTES.ONBOARDING}?from=banner`);
    },
  },

  // Add more conditional banners here...
];

// Storage key for tracking dismissed banners with expiry
const DISMISSED_BANNERS_KEY = 'dismissedBanners';

/**
 * Get dismissed banner IDs that are still valid
 */
const getDismissedBanners = (): Set<string> => {
  try {
    const stored = localStorage.getItem(DISMISSED_BANNERS_KEY);
    if (!stored) return new Set();
    
    const data: Record<string, number> = JSON.parse(stored);
    const now = Date.now();
    const validIds: string[] = [];
    
    // Filter out expired dismissals (legacy entries may have midnight expiry)
    Object.entries(data).forEach(([id, expiresAt]) => {
      if (expiresAt > now) {
        validIds.push(id);
      }
    });
    
    return new Set(validIds);
  } catch {
    return new Set();
  }
};

/**
 * Add a banner ID to the dismissed list permanently.
 * Exported so GlobalBanner and Onboarding can mark banners as dismissed.
 */
export const addDismissedBanner = (id: string): void => {
  try {
    const stored = localStorage.getItem(DISMISSED_BANNERS_KEY);
    const data: Record<string, number> = stored ? JSON.parse(stored) : {};
    
    // Persist permanently
    data[id] = Number.MAX_SAFE_INTEGER;
    
    localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
};

/**
 * GlobalBannerManager
 *
 * This component runs on app load and checks various conditions
 * to determine which global banners should be shown.
 *
 * It uses the touchpoint tracking API to determine user progress
 * instead of localStorage.
 *
 * It doesn't render anything - it just manages banner state via Redux.
 */
const GlobalBannerManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {user} = useAuth();
  const isAuthenticated = !!user;

  // Check if currently on onboarding route
  const isOnOnboardingRoute = location.pathname.startsWith(ROUTES.ONBOARDING);

  // Check if on a route where banners should be hidden (chat pages, etc.)
  const isOnExcludedRoute = EXCLUDED_BANNER_ROUTES.some(
    (route) => location.pathname.startsWith(route) || location.pathname.includes('/chat')
  );

  // Get onboarding completion status from touchpoint API
  const {isComplete: isOnboardingComplete, isLoading: isOnboardingLoading} =
    useIsOnboardingComplete();

  // Get the resume step for dynamic banner text
  const {firstIncompleteStep: resumeStep} = useOnboardingResumeStep();

  // Get profile field status for per-field banners
  const profileFields = useProfileFieldsStatus();

  // Get touchpoints that are due for prompting (for 90-day confirmation banners)
  const {touchpoints: promptableTouchpoints} = usePromptableTouchpoints();

  // Get all touchpoint summaries for checking completion status
  const {data: touchpointSummaries = []} = useTouchpointSummaries();

  // Mutation for logging touchpoint interactions
  const logTouchpointMutation = useLogTouchpoint();

  // Track which touchpoints we've already logged prompts for to avoid duplicates
  const promptedTouchpointsRef = useRef<Set<string>>(new Set());

  const logTouchpoint = useCallback(
    (slug: TouchpointSlug, action: 'prompt' | 'access' | 'complete') => {
      logTouchpointMutation.mutate({touchpoint: slug, action});
    },
    [logTouchpointMutation]
  );

  // Check conditions and show/hide banners
  useEffect(() => {
    const context: BannerContext = {
      user,
      isAuthenticated,
      isOnboardingComplete,
      isOnboardingLoading,
      navigate,
      isOnOnboardingRoute,
      isOnExcludedRoute,
      logTouchpoint,
      resumeStep,
      profileFields,
      promptableTouchpoints,
      touchpointSummaries,
    };

    // Get banners dismissed until midnight
    const dismissedBanners = getDismissedBanners();

    BANNER_CONDITIONS.forEach((condition) => {
      // Skip if user dismissed this banner (expires at midnight)
      if (dismissedBanners.has(condition.id)) {
        return;
      }

      const shouldShow = condition.check(context);

      if (shouldShow) {
        // Resolve banner - can be static object or function that returns banner
        const bannerConfig = typeof condition.banner === 'function' 
          ? condition.banner(context) 
          : condition.banner;
        
        // Create banner without onClick - just serializable data
        // The GlobalBanner component will look up handlers via useBannerActionHandler
        const bannerData: GlobalBannerNotification = {
          ...bannerConfig,
          id: condition.id,
        };
        dispatch(GlobalBannerActions.showBanner(bannerData));

        // Log that we prompted the user (if touchpoint-based and not already logged)
        if (condition.touchpointSlug && !promptedTouchpointsRef.current.has(condition.touchpointSlug)) {
          promptedTouchpointsRef.current.add(condition.touchpointSlug);
          logTouchpoint(condition.touchpointSlug, 'prompt');
        }
      } else {
        // Hide if condition no longer met
        dispatch(GlobalBannerActions.hideBanner(condition.id));
      }
    });
  }, [
    user,
    isAuthenticated,
    isOnboardingComplete,
    isOnboardingLoading,
    isOnOnboardingRoute,
    isOnExcludedRoute,
    navigate,
    logTouchpoint,
    dispatch,
    resumeStep,
    profileFields,
    promptableTouchpoints,
    touchpointSummaries,
  ]);

  // This component doesn't render anything
  return null;
};

/**
 * Hook to get the action handler for a banner.
 * This allows GlobalBanner to call actions without storing functions in Redux.
 */
export const useBannerActionHandler = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logTouchpointMutation = useLogTouchpoint();

  const logTouchpoint = useCallback(
    (slug: TouchpointSlug, action: 'prompt' | 'access' | 'complete') => {
      logTouchpointMutation.mutate({touchpoint: slug, action});
    },
    [logTouchpointMutation]
  );

  const handleAction = useCallback(
    (actionId: string) => {
      // Find the banner condition with this action
      const condition = BANNER_CONDITIONS.find((c) => c.id === actionId);
      if (condition?.onAction) {
        // Create a minimal context for the action
        // Action handlers only need navigate and logTouchpoint, but we include
        // all fields for type safety
        const context: BannerContext = {
          user: null,
          isAuthenticated: true,
          isOnboardingComplete: false,
          isOnboardingLoading: false,
          navigate,
          isOnOnboardingRoute: false,
          isOnExcludedRoute: false,
          logTouchpoint,
          resumeStep: null,
          profileFields: {
            home_address: {has_value: false},
            handle: {has_value: false},
            profile_photo: {has_value: false},
            allRequiredComplete: false,
            isLoading: false,
          },
          promptableTouchpoints: [],
          touchpointSummaries: [],
        };
        condition.onAction(context);
      }
      // Dismiss the banner after action
      dispatch(GlobalBannerActions.hideBanner(actionId));
    },
    [navigate, logTouchpoint, dispatch]
  );

  return handleAction;
};

export default GlobalBannerManager;
